import { ENTITY, UserActions } from '@/enums';
import useCheckScopeCombinations from '@/hooks/useCheckScopeCombinations';
import { IAddressDto, ProjectEntity } from '@/interfaces';
import { QueryKeyTypes, newQueryKeys } from '@/queries/queryKeys';
import { passPageParams } from '@/queries/utils';
import { projectService } from '@/services/api-services';
import { CustomResponse } from '@/services/api-services/type';
import {
  CreateProjectEntity,
  TriggerPdfGenerationParams,
  ProjectParams,
} from '@/types';
import {
  UseMutationOptions,
  UseQueryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import flatMap from 'lodash/flatMap';
import { useEffect, useMemo, useState } from 'react';
import { CustomInfiniteQueryOptions } from './type';
import { DEFAULT_PAGE_SIZE, PAGE_DEFAULT } from '@amili-v3/amili-v3-design';

type DefaultQuery = Partial<
  UseQueryOptions<
    CustomResponse<ProjectEntity[]>,
    Error,
    CustomResponse<ProjectEntity[]>,
    QueryKeyTypes<ProjectParams>
  >
>;

type DetailQuery = Partial<
  UseQueryOptions<ProjectEntity, Error, ProjectEntity, QueryKeyTypes>
>;

type AddressQuery = Partial<
  UseQueryOptions<IAddressDto, Error, IAddressDto, QueryKeyTypes>
>;

type InfiniteQuery = CustomInfiniteQueryOptions<
  CustomResponse<ProjectEntity[]>,
  ProjectParams
>;

type MutationCreate = UseMutationOptions<
  ProjectEntity,
  Error,
  CreateProjectEntity
>;

type MutationUpdate = UseMutationOptions<
  ProjectEntity,
  Error,
  Partial<ProjectEntity>
>;

type MutationDelete = UseMutationOptions<unknown, Error, string>;

type MutationGeneratePdf = UseMutationOptions<
  unknown,
  Error,
  TriggerPdfGenerationParams
>;

const useGetProjects = (
  props: { params?: ProjectParams; options?: DefaultQuery } = {},
) => {
  const { params = {}, options = {} } = props;
  const { canGetProjects } = useCheckScopeCombinations(
    [[{ entity: ENTITY.PROJECTS, action: UserActions.GET }]],
    { optimistic: false },
  );

  const query = useQuery({
    queryKey: newQueryKeys.project.list(passPageParams(params)),
    queryFn: projectService.get,
    ...options,
    enabled: (options.enabled ?? true) && canGetProjects,
  });

  return query;
};

const useGetInfiniteProjects = (
  props: { params?: ProjectParams; options?: InfiniteQuery } = {},
) => {
  const { params = {}, options = {} } = props;
  const queryKey = newQueryKeys.project.list(passPageParams(params));
  const { canGetProjects } = useCheckScopeCombinations(
    [[{ entity: ENTITY.PROJECTS, action: UserActions.GET }]],
    { optimistic: false },
  );

  const query = useInfiniteQuery({
    queryKey,
    queryFn: projectService.getInfinite,
    initialPageParam: 1,
    getNextPageParam: lastPage =>
      lastPage.hasNextPage ? lastPage.page + 1 : undefined,
    ...options,
    enabled: (options.enabled ?? true) && canGetProjects,
  });

  const flatData = flatMap(query?.data?.pages?.map(p => p.data));

  return { ...query, flatData };
};

const useGetProjectAddress = (props: {
  id: string;
  options?: AddressQuery;
}) => {
  const { id, options = {} } = props ?? {};
  const { canGetProjects } = useCheckScopeCombinations(
    [[{ entity: ENTITY.PROJECTS, action: UserActions.GET }]],
    { optimistic: false },
  );

  const query = useQuery({
    queryKey: newQueryKeys.project.detail(id, { subScope: 'address' }),
    queryFn: projectService.getAddressById,
    ...options,
    enabled: (options.enabled ?? !!id) && canGetProjects,
  });

  return query;
};

const useGetProjectById = (props: { id: string; options?: DetailQuery }) => {
  const { id, options = {} } = props ?? {};
  const { canGetProjects } = useCheckScopeCombinations(
    [[{ entity: ENTITY.PROJECTS, action: UserActions.GET }]],
    { optimistic: false },
  );

  const projectQuery = useQuery({
    queryKey: newQueryKeys.project.detail(id),
    queryFn: projectService.getById,
    ...options,
    enabled: (options.enabled ?? true) && canGetProjects,
  });

  const addressQuery = useGetProjectAddress({ id });

  const data = useMemo<ProjectEntity | undefined>(() => {
    if (!projectQuery.data) return projectQuery.data;
    return { ...projectQuery.data, address: addressQuery.data };
  }, [projectQuery.data, addressQuery.data]);

  return {
    ...projectQuery,
    data,
    isLoading: projectQuery.isLoading,
    isPending: projectQuery.isPending || addressQuery.isPending,
  };
};

const useCreateProject = (options: MutationCreate = {}) => {
  const mutation = useMutation({
    mutationFn: projectService.create,
    mutationKey: newQueryKeys.project.invalidateListsKey(),
    ...options,
  });

  return mutation;
};

const useUpdateProject = (options: MutationUpdate = {}) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...rest } = options;

  const mutation = useMutation({
    mutationFn: projectService.update,
    mutationKey: newQueryKeys.project.invalidateListsKey(),
    onSuccess: (data, variables, context) => {
      const { id = '' } = variables;
      onSuccess?.(data, variables, context);
      queryClient.invalidateQueries({
        queryKey: newQueryKeys.project.detail(id),
      });
    },
    ...rest,
  });

  return mutation;
};

const useDeleteProject = (options: MutationDelete = {}) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...rest } = options;

  const mutation = useMutation({
    mutationFn: projectService.delete,
    mutationKey: newQueryKeys.project.invalidateListsKey(),
    onSuccess: (data, id, context) => {
      onSuccess?.(data, id, context);
      queryClient.invalidateQueries({
        queryKey: newQueryKeys.project.detail(id),
      });
    },
    ...rest,
  });

  return mutation;
};

const useGeneratePdfMutation = (options: MutationGeneratePdf = {}) => {
  const mutation = useMutation({
    mutationFn: projectService.generatePdf,
    mutationKey: newQueryKeys.project.invalidateListsKey(),
    ...options,
  });

  return mutation;
};

// type ProjectWithRelationsType = UseQueryOptions<
//   ProjectEntity[],
//   Error,
//   ProjectEntity[],
//   QueryKeyTypes<ProjectParams>
// >;
const useGetProjectsWithRelations = (
  params: ProjectParams,
  options: DefaultQuery = {},
) => {
  const { canGetProjects } = useCheckScopeCombinations(
    [[{ entity: ENTITY.PROJECTS, action: UserActions.GET }]],
    { optimistic: false },
  );
  //Set up variables to update pagination
  const [page, setPage] = useState(PAGE_DEFAULT);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  useEffect(() => {
    const passParams = passPageParams(params);
    setPage(passParams.page);
    setPageSize(passParams.take);
  }, [params]);

  //get params value
  const queryParams = useMemo<ProjectParams>(
    () => ({
      ...params,
      take: pageSize,
      page,
    }),
    [page, pageSize, params],
  );

  const query = useQuery({
    queryKey: newQueryKeys.project.list(queryParams),
    queryFn: projectService.getProjectsWithRelations,
    ...options,
    enabled: (options.enabled ?? true) && canGetProjects,
  });

  return {
    ...query,
    pageSetting: { page, setPage, takePageSize: pageSize, setPageSize },
  };
};

export {
  useCreateProject,
  useDeleteProject,
  useGetInfiniteProjects,
  useGetProjectAddress,
  useGetProjectById,
  useGetProjects,
  useGetProjectsWithRelations,
  useUpdateProject,
  useGeneratePdfMutation,
};
